/*
    Starter artwork for a postcard (MPN-1774).

    Rachel, on the empty HTML box: "I wouldn't know what to put here right now outside of
    going into Lob to create a template and copy/pasting the HTML here." That is the problem
    these solve. Picking one fills both sides with correct, print-safe HTML she can then edit,
    so the starting point is a working card rather than a blank textarea and a trip to another
    product.

    Deliberately plain HTML and not a builder. The printer renders HTML per recipient -- that
    is the only reason a card can carry someone's name -- so HTML is the real artifact, and a
    drag-and-drop layer over it would hide the thing she has to be able to edit. Once she saves
    it the ad is reusable content in the catalog, which is where "her own templates" already
    live.

    THE THREE THINGS THAT MAKE A CARD PRINTABLE, all verified by rendering through Lob's test
    key rather than read off a spec sheet:

    1. Design on the full-bleed canvas and expect to lose 0.125in on every edge. We send
       6.25x4.25in for a "4x6" and Lob returns a PDF trimmed to exactly 6x4. Anything closer
       than 0.25in to the canvas edge is gambling.

    2. The back is not a blank canvas. Lob stamps the address, the USPS barcode and the
       postage indicia into an area anchored bottom-right, over whatever we drew there. The
       usable space is the L around it: a full-width strip along the top, and a column down
       the left.

    3. The QR code lands bottom-left, at 0.5in in and 1.5in wide, placed by the server (see
       providers/lob/layout.py). These templates leave that square empty. It is drawn after
       our artwork, so anything underneath is simply covered.

    Merge variables use mustache sections rather than a bare {{first_name}}, because a person
    with no first name on file sends an empty string and a bare tag prints "Hi ,". The
    inverted section gives those people "Hi there," instead. Both branches confirmed against
    a real render.

    WHAT THE PRINTER'S BROWSER CAN ACTUALLY DO, probed by rendering a card of test rows:

      works: absolute positioning with top/right/bottom/left, background colours, rgba(),
             linear-gradient, border-radius, text-transform, letter-spacing
      does not: `inset` shorthand, and flexbox -- `display:flex` lays children out inline and
             ignores `flex`, silently

    It is an older engine than the one in your browser, and it fails quietly rather than
    loudly: the first draft of these templates used `inset: 0` for the background and flexbox
    to centre the copy, and Lob returned a blank white card with invisible white text on it.
    So: no flexbox, no grid, no `inset`, and copy anchored to an edge rather than centred.
    If you add a template, render it through the test key before trusting it.
*/

export type MailPieceSize = "4x6" | "6x9" | "6x11";

/** The artwork we send. Lob trims 0.125in off each edge to reach the named size. */
export const CANVAS: Record<MailPieceSize, { width: number; height: number }> = {
    "4x6": { width: 6.25, height: 4.25 },
    "6x9": { width: 9.25, height: 6.25 },
    "6x11": { width: 11.25, height: 6.25 },
};

/**
 * Lob's reserved area on the back: address, barcode and indicia, anchored bottom-right.
 * Mirrors `delivery/providers/lob/layout.py` on the server -- the same numbers, because the
 * server positions the QR code against them and this lays the artwork out against them.
 */
export const INK_FREE_ZONE: Record<MailPieceSize, {
    width: number; height: number; right: number; bottom: number;
}> = {
    "4x6": { width: 3.2835, height: 2.375, right: 0.275, bottom: 0.25 },
    "6x9": { width: 4.0, height: 2.375, right: 0.275, bottom: 0.25 },
    "6x11": { width: 4.0, height: 2.375, right: 0.275, bottom: 0.25 },
};

/** Cut off every edge on its way to the named size. Confirmed against a returned PDF. */
export const TRIM = 0.125;

/**
 * Where Lob puts the QR code, in canvas inches like everything else here.
 *
 * MEASURED, not assumed. Rendered through the test key on 22 Sep 2026 and read back off the
 * returned PDF at 600dpi:
 *
 *   - Lob positions the code against the FINISHED card, not the bleed canvas. We send
 *     `left: 0.5` and it lands 0.500in from the trimmed edge -- so in canvas inches that is
 *     0.5 + TRIM. Reserving it at 0.5 of the canvas, as this used to, put the square 0.125in
 *     from where the code actually prints.
 *   - Lob draws its own white plate behind the code, exactly the requested width, with the
 *     quiet zone inside it. A 1in code occupies a 1.000 x 1.000in white square. So `printed`
 *     equals `size`; the older note that a 1.5in code occupied 1.65in was wrong.
 *   - The code reads at 0.9in and does not at 0.85in, so 1in carries real margin.
 *
 * Because Lob supplies the plate, artwork needs no light panel of its own -- a code on a navy
 * card scanned perfectly with nothing behind it.
 */
export const QR_AREA = {
    left: 0.5 + TRIM,
    bottom: 0.5 + TRIM,
    size: 1.0,
    /** Lob's white plate is exactly the requested width. */
    printed: 1.0,
};

/**
 * Below this a printed code stops being read.
 *
 * Measured rather than quoted: 0.9in decoded off a 600dpi render of Lob's own PDF and 0.85in
 * did not. The shipped size is 1in, which leaves margin for a phone camera, a worse print and
 * a card that has been through the post.
 */
export const MIN_QR_SIZE = 0.9;

/**
 * Margin from the canvas edge. Trim eats {@link TRIM} of it, so this leaves 0.25in on the
 * finished card, which is the margin that actually matters.
 */
export const SAFE_MARGIN = TRIM + 0.25;

/** Greeting that still reads correctly for a person with no first name on file. */
export const GREETING =
    "{{#first_name}}Hi {{first_name}},{{/first_name}}{{^first_name}}Hi there,{{/first_name}}";

/** Top of the reserved area, measured from the bottom of the canvas. */
function reservedTop(size: MailPieceSize): number {
    const zone = INK_FREE_ZONE[size];
    return zone.bottom + zone.height;
}

/** The full-width strip above the reserved area, which is where back copy goes. */
function backStripHeight(size: MailPieceSize): number {
    return CANVAS[size].height - SAFE_MARGIN - reservedTop(size) - 0.1;
}

/**
 * How wide a paragraph is allowed to get, in inches.
 *
 * Capped at a readable measure rather than the width of the card. A line of 12pt text
 * running the full 10.25in of a 6x11 is about 110 characters, which the eye loses its place
 * on; a fixed width instead looks cramped on the small card. So: the narrower of a good
 * measure and the space available.
 */
function proseWidth(size: MailPieceSize): number {
    return Math.min(6, CANVAS[size].width - SAFE_MARGIN * 2);
}

function shell(size: MailPieceSize, css: string, body: string): string {
    const { width, height } = CANVAS[size];
    return `<html>
<head>
<style>
  /* Lob renders this as a page, so the page itself must have no margin of its own. */
  @page { margin: 0; }
  html, body { margin: 0; padding: 0; }

  /* The full-bleed canvas. Lob trims ${TRIM}in off every edge to reach ${size}. */
  .card {
    position: relative;
    width: ${width}in;
    height: ${height}in;
    box-sizing: border-box;
    overflow: hidden;
    /* System faces only. A downloaded web font may not arrive before the page is
       rasterised, and the card silently prints in a fallback. */
    font-family: "Helvetica Neue", Helvetica, Arial, sans-serif;
    color: #10233f;
  }

  /* Everything that matters stays inside this. */
  .safe { position: absolute; top: ${SAFE_MARGIN}in; right: ${SAFE_MARGIN}in;
          bottom: ${SAFE_MARGIN}in; left: ${SAFE_MARGIN}in; }
${css}
</style>
</head>
<body>
  <div class="card">
${body}
  </div>
</body>
</html>`;
}

/**
 * The back, laid out around the parts of it we are not allowed to use.
 *
 * `copy` goes in the strip above the reserved area; `caption` sits just above the QR code.
 */
function back(size: MailPieceSize, opts: {
    accent: string; copy: string; caption: string;
}): string {
    const captionBottom = QR_AREA.bottom + QR_AREA.printed + 0.12;
    return shell(size, `
  .msg {
    position: absolute; left: ${SAFE_MARGIN}in; right: ${SAFE_MARGIN}in;
    bottom: ${reservedTop(size) + 0.1}in; height: ${backStripHeight(size)}in;
    font-size: 10.5pt; line-height: 1.45; max-width: ${proseWidth(size)}in;
  }
  .msg strong { color: ${opts.accent}; }

  /* Sits above the QR code, clear of the quiet zone Lob draws around it. */
  .scan {
    position: absolute; left: ${QR_AREA.left}in; bottom: ${captionBottom}in;
    width: ${QR_AREA.size + 0.4}in;
    font-size: 8pt; font-weight: bold; letter-spacing: 0.02em;
    text-transform: uppercase; color: ${opts.accent};
  }

  /* Left empty on purpose: Lob prints the address, barcode and postage here, on top of
     anything we draw. Keep artwork out of it. */
  .reserved {
    position: absolute; right: ${INK_FREE_ZONE[size].right}in;
    bottom: ${INK_FREE_ZONE[size].bottom}in;
    width: ${INK_FREE_ZONE[size].width}in; height: ${INK_FREE_ZONE[size].height}in;
  }`, `    <div class="msg">${opts.copy}</div>
    <div class="scan">${opts.caption}</div>
    <div class="reserved"></div>`);
}

export interface MailPieceTemplate {
    id: string;
    name: string;
    /** One line, shown under the name in the picker. */
    description: string;
    build(size: MailPieceSize): { front: string; back: string };
}

export const MAIL_PIECE_TEMPLATES: MailPieceTemplate[] = [
    {
        id: "deadline",
        name: "Deadline reminder",
        description: "A date and one action. For registration closing or a last day to enrol.",
        build: (size) => ({
            front: shell(size, `
  .bg { position: absolute; top: 0; right: 0; bottom: 0; left: 0; background: #0b2545; }
  /* Anchored to the bottom rather than centred: the renderer has no flexbox, and an edge
     is something it can position exactly. */
  .inner { position: absolute; left: 0; right: 0; bottom: 0; }
  .kicker { font-size: 10pt; letter-spacing: 0.14em; text-transform: uppercase;
            color: #7fb2f0; margin: 0 0 0.12in; }
  .headline { font-size: ${size === "4x6" ? 28 : 42}pt; line-height: 1.04;
              font-weight: bold; color: #ffffff; margin: 0 0 0.14in; }
  .sub { font-size: 12pt; color: #c7dcf7; margin: 0; max-width: ${proseWidth(size)}in; }`,
                `    <div class="bg"></div>
    <div class="safe"><div class="inner">
      <p class="kicker">Registration closes soon</p>
      <h1 class="headline">Classes fill up.<br>Yours does not have to.</h1>
      <p class="sub">Spring registration is open now. It takes about ten minutes.</p>
    </div></div>`),
            back: back(size, {
                accent: "#0b2545",
                copy: `<p style="margin:0 0 0.08in">${GREETING}</p>
      <p style="margin:0">You still have time to register for spring classes, and advising is
      free. Scan the code to see what is open, or call <strong>555-0100</strong> and someone
      will walk you through it.</p>`,
                caption: "Scan to register",
            }),
        }),
    },
    {
        id: "invite",
        name: "Personal invite",
        description: "A warm opener addressed to the person. For a first touch or a re-engage.",
        build: (size) => ({
            front: shell(size, `
  .bg { position: absolute; top: 0; right: 0; bottom: 0; left: 0; background: #fdfbf6; }
  .rule { position: absolute; left: ${SAFE_MARGIN}in; top: ${SAFE_MARGIN}in;
          width: 0.9in; height: 0.06in; background: #b4551f; }
  .inner { position: absolute; left: 0; right: 0; bottom: 0; }
  .headline { font-size: ${size === "4x6" ? 24 : 36}pt; line-height: 1.12;
              font-weight: bold; margin: 0 0 0.16in; color: #2a1a10; }
  .sub { font-size: 12pt; line-height: 1.45; color: #5b4636; margin: 0;
         max-width: ${proseWidth(size)}in; }`,
                `    <div class="bg"></div>
    <div class="rule"></div>
    <div class="safe"><div class="inner">
      <h1 class="headline">${GREETING}<br>there is a seat here for you.</h1>
      <p class="sub">Whatever you are working toward, there is a path to it that starts this
      term.</p>
    </div></div>`),
            back: back(size, {
                accent: "#b4551f",
                copy: `<p style="margin:0">Talk to an advisor before you decide anything. No
      application, no cost, no commitment: just a conversation about what you want to do next
      and the shortest way to get there. Scan the code to pick a time that suits you.</p>`,
                caption: "Scan to book a time",
            }),
        }),
    },
    {
        id: "blank",
        name: "Blank card",
        description: "The correct structure with nothing in it. For building your own.",
        build: (size) => ({
            front: shell(size, `
  .bg { position: absolute; top: 0; right: 0; bottom: 0; left: 0; background: #ffffff; }
  .inner { position: absolute; left: 0; right: 0; bottom: 0; }`,
                `    <div class="bg"></div>
    <!-- Everything inside .safe is clear of the trim. Put artwork on .bg if you want it
         to run off the edge of the card. -->
    <div class="safe"><div class="inner">
      <h1 style="font-size:28pt;margin:0 0 0.12in">Headline</h1>
      <p style="font-size:12pt;margin:0;color:#4a5568">Supporting line.</p>
    </div></div>`),
            back: back(size, {
                accent: "#334155",
                copy: `<p style="margin:0 0 0.08in">${GREETING}</p>
      <p style="margin:0">Your message. Keep it in this strip: the block below and to the
      right is where the address and barcode print.</p>`,
                caption: "Scan to learn more",
            }),
        }),
    },
];

export function templateById(id: string): MailPieceTemplate | undefined {
    return MAIL_PIECE_TEMPLATES.find((t) => t.id === id);
}

/**
 * Fill merge tags with stand-in values, for previewing only.
 *
 * A preview showing `{{#first_name}}Hi {{first_name}},{{/first_name}}` tells you nothing
 * about the card and looks like something is broken. This resolves the sections the way the
 * printer will, so what you see is a sentence.
 *
 * Deliberately a small substitution and not a mustache engine: these are the only tags a
 * mail piece has, the result is never sent anywhere, and a dependency that renders untrusted
 * markup for a cosmetic preview is a poor trade.
 */
export function withSampleData(html: string, sample = "Jordan"): string {
    return html
        // Present branch: keep the body, the inverted branch goes.
        .replace(/\{\{#first_name\}\}([\s\S]*?)\{\{\/first_name\}\}/g, "$1")
        .replace(/\{\{\^first_name\}\}[\s\S]*?\{\{\/first_name\}\}/g, "")
        .replace(/\{\{first_name\}\}/g, sample)
        .replace(/\{\{last_name\}\}/g, "Reyes")
        .replace(/\{\{code\}\}/g, "abc123");
}

/**
 * A card that is one uploaded picture (MPN direct mail).
 *
 * The whole point of uploading straight onto a side: somebody already has finished artwork
 * and wants it on the card without routing it through a design tool to get there.
 *
 * THE GEOMETRY IS WORKED OUT HERE, not handed to the renderer. `object-fit` is the obvious
 * way to write this and Lob's engine is far too old for it -- the same engine that ignores
 * flexbox and `inset`. It would have been ignored silently and the image would have printed
 * stretched to the card's proportions. So the scale is computed from the image's real size
 * and emitted as explicit inches.
 *
 * `fit` behaves as it does everywhere else: "fill" covers the card and crops the overflow,
 * which is what full bleed means; "fit" keeps every pixel and pads the rest.
 *
 * The URL must be a durable one. A presigned S3 link lasts six hours and Lob fetches the
 * image when it renders the piece, days later -- printing the card without it and saying
 * nothing. Pass the media's `artwork_url`, never its `storage_uri`.
 */
export function imageCardHtml(
    imageUrl: string,
    opts: {
        size: MailPieceSize;
        /** The picture's own pixel dimensions, read from the file after it loads. */
        naturalWidth: number;
        naturalHeight: number;
        fit?: "fill" | "fit";
        background?: string;
        /**
         * Which side this is. Nothing here reserves space for the QR code: Lob draws its own
         * white plate behind it, verified by rendering a code onto a navy card with nothing
         * underneath and reading it back off the returned PDF.
         */
        side?: "front" | "back";
    },
): string {
    const card = CANVAS[opts.size];
    const fill = (opts.fit ?? "fill") === "fill";

    // Inches per pixel, chosen so the picture either covers the card or sits inside it.
    const byWidth = card.width / Math.max(1, opts.naturalWidth);
    const byHeight = card.height / Math.max(1, opts.naturalHeight);
    const scale = fill ? Math.max(byWidth, byHeight) : Math.min(byWidth, byHeight);

    const drawnWidth = opts.naturalWidth * scale;
    const drawnHeight = opts.naturalHeight * scale;
    // Centred, so a crop takes evenly from both edges rather than all from one.
    const left = (card.width - drawnWidth) / 2;
    const top = (card.height - drawnHeight) / 2;

    const round = (v: number) => (Math.round(v * 1000) / 1000).toString();

    return `<html>
<head>
<style>
  @page { margin: 0; }
  html, body { margin: 0; padding: 0; }
  /* Full bleed. Lob trims ${TRIM}in off every edge to reach ${opts.size}. */
  .card {
    position: relative;
    width: ${card.width}in; height: ${card.height}in;
    overflow: hidden;
    background: ${opts.background || "#ffffff"};
  }
  /* Placed and sized outright. Nothing here is left for the renderer to work out. */
  .art {
    position: absolute;
    left: ${round(left)}in; top: ${round(top)}in;
    width: ${round(drawnWidth)}in; height: ${round(drawnHeight)}in;
  }
</style>
</head>
<body>
  <div class="card">
    <img class="art" src="${imageUrl}" alt="" />
  </div>
</body>
</html>`;
}

/*
    Where a QR code is allowed to sit, and how big.

    MIRRORS `delivery/providers/lob/layout.py` ON THE SERVER, deliberately and by hand. The
    server is the authority -- it validates every save and it is what builds the Lob payload --
    but the drag UI has to know the same rules to clamp against, and a round trip per pointer
    move is not a drag. The two are kept honest by a test on each side asserting the same
    numbers, so a change to one fails the other.

    EVERYTHING HERE IS IN FINISHED-CARD INCHES, measured from the bottom-left. Lob measures its
    `qr_code` parameter against the trimmed card rather than the canvas we send, and mixing the
    two coordinate systems is exactly the bug that once printed a code under the barcode
    (MPN-1773). Convert with {@link qrCanvasOrigin} before drawing on the canvas.
*/

/** Below this a printed code stops decoding. 0.9in decoded off a real print, 0.85in did not. */
export const QR_MIN_WIDTH = 0.9;

/** Not a print limit: a code larger than this leaves the card nothing to say. */
export const QR_MAX_WIDTH = 2.0;

/** How far a code must stay from the finished edge, matching the margin copy keeps. */
export const QR_EDGE_MARGIN = 0.25;

/** The platform default, and where every card printed before placement was editable. */
export const QR_DEFAULT = { left: 0.5, bottom: 0.5, width: 1.0 };

export interface QrPlacement {
    left: number;
    bottom: number;
    width: number;
}

/** The card as it arrives, after Lob trims {@link TRIM} off every edge. */
export function finishedSize(size: MailPieceSize): { width: number; height: number } {
    const canvas = CANVAS[size] ?? CANVAS["4x6"];
    return { width: canvas.width - 2 * TRIM, height: canvas.height - 2 * TRIM };
}

/**
 * Lob's address block, barcode and indicia, in finished-card inches from the bottom-left.
 *
 * `INK_FREE_ZONE` is measured on the canvas, so it has to be converted rather than compared
 * directly with a placement.
 */
export function reservedArea(size: MailPieceSize) {
    const zone = INK_FREE_ZONE[size] ?? INK_FREE_ZONE["4x6"];
    const card = finishedSize(size);
    const right = zone.right - TRIM;
    const bottom = zone.bottom - TRIM;
    return {
        left: card.width - right - zone.width,
        right: card.width - right,
        bottom,
        top: bottom + zone.height,
    };
}

/** Whether Lob would print its address block over a code placed here. */
export function overlapsReserved(size: MailPieceSize, p: QrPlacement): boolean {
    const zone = reservedArea(size);
    return !(
        p.left + p.width <= zone.left
        || p.left >= zone.right
        || p.bottom + p.width <= zone.bottom
        || p.bottom >= zone.top
    );
}

/**
 * Why this placement is not allowed, or null.
 *
 * Wording matches the server's, because the same sentence has to make sense whether it
 * arrives from a validation response or is shown live while dragging.
 */
export function qrPlacementError(size: MailPieceSize, p: QrPlacement): string | null {
    const card = finishedSize(size);
    if (p.width < QR_MIN_WIDTH) {
        return `A QR code narrower than ${QR_MIN_WIDTH}in does not reliably scan off a printed card.`;
    }
    if (p.width > QR_MAX_WIDTH) {
        return `A QR code wider than ${QR_MAX_WIDTH}in leaves no room for the message.`;
    }
    if (p.left < QR_EDGE_MARGIN || p.bottom < QR_EDGE_MARGIN) {
        return `Keep the QR code at least ${QR_EDGE_MARGIN}in from the edge of the card.`;
    }
    if (p.left + p.width > card.width - QR_EDGE_MARGIN) return "The QR code runs off the right edge.";
    if (p.bottom + p.width > card.height - QR_EDGE_MARGIN) return "The QR code runs off the top edge.";
    if (overlapsReserved(size, p)) {
        return "The QR code overlaps the area the post office needs for the address and "
            + "barcode. Lob prints those on top, so the code would be covered.";
    }
    return null;
}

/**
 * The nearest allowed placement to the one asked for.
 *
 * Used while dragging, so it has to answer for every position a pointer can reach rather than
 * only for legal ones. Edges clamp; the reserved area pushes out along whichever axis the code
 * has least distance to travel, which is what makes dragging past the address block feel like
 * sliding around it instead of sticking.
 */
export function clampQrPlacement(size: MailPieceSize, p: QrPlacement): QrPlacement {
    const card = finishedSize(size);
    const width = round2(Math.min(QR_MAX_WIDTH, Math.max(QR_MIN_WIDTH, p.width)));
    const maxLeft = card.width - QR_EDGE_MARGIN - width;
    const maxBottom = card.height - QR_EDGE_MARGIN - width;

    // Rounded before the overlap test, not after. Rounding a computed escape position
    // afterwards moves it back the way it came -- 1.5665 becomes 1.57, and a 1in code there
    // ends at 2.57, which is inside a reserved area starting at 2.5665. That put the code
    // back under the address block for a whole band of drag positions.
    let left = round2(Math.min(Math.max(p.left, QR_EDGE_MARGIN), maxLeft));
    let bottom = round2(Math.min(Math.max(p.bottom, QR_EDGE_MARGIN), maxBottom));

    if (overlapsReserved(size, { left, bottom, width })) {
        const zone = reservedArea(size);
        // Each escape rounds AWAY from the reserved area, so rounding can only ever make the
        // gap bigger. Four ways out; the shortest that is itself on the card wins, which is
        // what makes dragging past the address block feel like sliding around it.
        const options = [
            { left: floor2(zone.left - width), bottom },
            { left: ceil2(zone.right), bottom },
            { left, bottom: ceil2(zone.top) },
            { left, bottom: floor2(zone.bottom - width) },
        ].filter((o) =>
            o.left >= QR_EDGE_MARGIN
            && o.bottom >= QR_EDGE_MARGIN
            && o.left <= maxLeft
            && o.bottom <= maxBottom
            && !overlapsReserved(size, { ...o, width }));

        if (options.length) {
            const best = options.reduce((a, b) =>
                Math.hypot(a.left - left, a.bottom - bottom) <= Math.hypot(b.left - left, b.bottom - bottom)
                    ? a : b);
            left = best.left;
            bottom = best.bottom;
        }
    }
    return { left, bottom, width };
}

/** Two decimals. A placement is a print instruction, not a float with a tail of noise. */
function round2(value: number): number {
    return Math.round(value * 100) / 100;
}

/** Rounded away from the reserved area, so rounding can only widen the gap. */
function floor2(value: number): number {
    return Math.floor(value * 100) / 100;
}

function ceil2(value: number): number {
    return Math.ceil(value * 100) / 100;
}

/** A finished-card placement expressed on the bleed canvas, for drawing. */
export function qrCanvasOrigin(p: QrPlacement) {
    return { left: p.left + TRIM, bottom: p.bottom + TRIM, size: p.width };
}
